/**
 * Application identifiers used throughout the system.
 * These are the canonical app names passed in custom tokens and stored in user claims.
 *
 * @type {Object<string, string>}
 */
const TARGET_APPS = Object.freeze({
    EDUCATION: 'education',
    TAP: 'tap',
    ADMIN: 'admin',
    PERSONAL: 'personal',
});

/**
 * Firestore database identifiers.
 * Maps to actual named databases in the Firebase project.
 * Use these constants when calling getFirestore(app, databaseId) or in database-routing logic.
 *
 * @type {Object<string, string>}
 */
const DATABASES = Object.freeze({
    DEFAULT: '(default)',
    TAP: 'tap-db',
    ADMIN: 'admin',
    CONTENT: 'content-db',
    PERSONAL: 'personal-db',
});

/**
 * Mapping of app identifiers to their Firestore database.
 * Use this to determine which database an app should read from/write to.
 *
 * Example:
 *   const dbId = DATABASES_BY_APP[TARGET_APPS.TAP]; // 'tap-db'
 *   const db = getFirestore(firebaseApp, dbId);
 *
 * @type {Object<string, string>}
 */
const DATABASES_BY_APP = Object.freeze({
    [TARGET_APPS.EDUCATION]: DATABASES.DEFAULT,
    [TARGET_APPS.TAP]: DATABASES.TAP,
    [TARGET_APPS.ADMIN]: DATABASES.ADMIN,
    [TARGET_APPS.PERSONAL]: DATABASES.PERSONAL,
});

/**
 * List of all valid application identifiers.
 * Use this for validation (e.g. checking if a user's app claim is known).
 *
 * @type {string[]}
 */
const KNOWN_APPS = Object.freeze(Object.values(TARGET_APPS));

export { TARGET_APPS, DATABASES, DATABASES_BY_APP, KNOWN_APPS };
